import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  Chip,
  TextField,
  CircularProgress,
  Autocomplete,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ASNToDelete } from '../utils/types';
import { SSCCProps } from '../../../datastore/ssccs';

interface DeleteASNDialogProps {
  open: boolean;
  selectedASN: ASNToDelete | null;
  selectedTransferSSCCId: string;
  selectedTransferReceiptedEvent: string;
  availableTransferSSCCs: SSCCProps[];
  isProcessing: boolean;
  onClose: () => void;
  onTransferSSCCIdChange: (ssccId: string) => void;
  onTransferReceiptedEventChange: (receiptedEvent: string) => void;
  onConfirm: (reason: string) => void;
}

export default function DeleteASNDialog({
  open,
  selectedASN,
  selectedTransferSSCCId,
  selectedTransferReceiptedEvent,
  availableTransferSSCCs,
  isProcessing,
  onClose,
  onTransferSSCCIdChange,
  onTransferReceiptedEventChange,
  onConfirm
}: DeleteASNDialogProps) {
  const [reasonDraft, setReasonDraft] = useState('');

  useEffect(() => {
    if (open) {
      setReasonDraft('');
    }
  }, [open]);

  const isDuplicate = selectedASN?.isDuplicate === true;
  const isPriorityShipping = selectedASN?.priorityShipping === true;
  const requiresTransferSelection = !isDuplicate;
  const showTransferSelection = requiresTransferSelection;
  const selectedTargetSSCC = availableTransferSSCCs.find((sscc) => sscc.id === selectedTransferSSCCId) || null;
  const receiptedEventOptions =
    selectedTargetSSCC?.receiptedEvents?.map(
      (event, index) => `${index + 1}. ${event.name || 'Unknown'} - ${event.site || 'Unknown'}`
    ) || [];
  const cancelDisabledHint = isProcessing ? 'Please wait until the current action finishes.' : '';
  const confirmDisabledHint = isProcessing
    ? 'Delete is in progress.'
    : !reasonDraft.trim()
      ? 'Reason for deletion is required.'
      : showTransferSelection && !selectedTransferSSCCId
        ? 'Select a target SSCC to continue.'
        : showTransferSelection && !selectedTransferReceiptedEvent
          ? 'Select a receipted event to continue.'
          : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
          color: '#fff',
          px: 3,
          py: 3
        }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              backdropFilter: 'blur(10px)'
            }}>
            <DeleteIcon sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
              Delete ASN
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}>
              Permanently remove this ASN
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, '& > :first-of-type': { mt: 1 } }}>
        <Stack spacing={3}>
          <Alert
            severity="warning"
            icon={<ErrorOutlineIcon />}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(237, 108, 2, 0.2)',
              backgroundColor: 'rgba(237, 108, 2, 0.04)',
              fontSize: '0.875rem',
              '& .MuiAlert-icon': {
                color: '#ed6c02'
              }
            }}>
            {isDuplicate
              ? 'Duplicate ASN detected. Continue with the standard delete flow.'
              : requiresTransferSelection
                ? 'This ASN is not duplicated. Please choose a target SSCC for transfer.'
                : 'This action will permanently mark the ASN as deleted. This action cannot be undone.'}
          </Alert>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              border: '2px solid rgba(211, 47, 47, 0.15)'
            }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#d32f2f'
                  }}>
                  ASN
                </Typography>
                <Typography variant="h6" fontWeight={600} sx={{ mt: 1, color: '#c62828' }}>
                  {selectedASN?.asn.asn || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#d32f2f'
                  }}>
                  Current Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedASN?.asnStatus}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#d32f2f'
                  }}>
                  Priority
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    label={selectedASN?.priority || 'N/A'}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                  <Chip
                    label={isPriorityShipping ? 'Priority Shipping: Yes' : 'Priority Shipping: No'}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: isPriorityShipping ? 'rgba(211, 47, 47, 0.12)' : 'rgba(25, 118, 210, 0.12)',
                      color: isPriorityShipping ? '#c62828' : '#1565c0'
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          {requiresTransferSelection && (
            <Stack spacing={2}>
              {showTransferSelection && (
                <>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 1.5,
                        fontSize: '0.875rem'
                      }}>
                      Select Target SSCC{' '}
                      <Box component="span" sx={{ color: 'error.main' }}>
                        *
                      </Box>
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        mb: 1,
                        fontSize: '0.75rem'
                      }}>
                      Eligible target SSCCs are unassigned SSCCs, excluding the current source SSCC.
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        mb: 1,
                        fontSize: '0.75rem'
                      }}>
                      Option 1 (Best practice): open the SSCC where this ASN should belong, add the ASN there, then
                      return here to delete the duplicate ASN.
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        mb: 1,
                        fontSize: '0.75rem'
                      }}>
                      Option 2 (Transfer first, then delete here): if you cannot find a suitable SSCC, create one in
                      Manage SSCCs and assign the ASN there as a temporary holding location. FTL currently does not
                      support storing an ASN without an SSCC.
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        mb: 1,
                        fontSize: '0.75rem'
                      }}>
                      After the transfer is done, come back here to delete the duplicate ASN, then complete that SSCC if
                      needed.
                    </Typography>
                    <Autocomplete
                      options={availableTransferSSCCs}
                      getOptionLabel={(option) =>
                        `${option.id}${option.nickname ? ` (${option.nickname})` : ''} - ${option.status || 'Unknown'}`
                      }
                      value={availableTransferSSCCs.find((sscc) => sscc.id === selectedTransferSSCCId) || null}
                      onChange={(_, newValue) => onTransferSSCCIdChange(newValue?.id || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search target SSCC..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {option.id}
                                {option.nickname ? ` (${option.nickname})` : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {option.status || 'Unknown'}
                              </Typography>
                            </Stack>
                          </li>
                        );
                      }}
                    />
                  </Box>

                  {selectedTargetSSCC && (
                    <Stack spacing={2}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 1.5,
                          fontSize: '0.875rem'
                        }}>
                        Select Receipted Event to be Added To{' '}
                        <Box component="span" sx={{ color: 'error.main' }}>
                          *
                        </Box>
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: 'text.secondary', mb: 1, fontSize: '0.75rem' }}>
                        Eligible receipted events are events that already exist on the selected target SSCC.
                      </Typography>
                      <Autocomplete
                        options={receiptedEventOptions}
                        value={selectedTransferReceiptedEvent || null}
                        onChange={(_, newValue) => onTransferReceiptedEventChange(newValue || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select receipted event..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '0.875rem'
                              }
                            }}
                          />
                        )}
                        noOptionsText="No receipted events available"
                      />
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Reason for Deletion{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              placeholder="Explain why this ASN is being deleted (e.g., duplicate, erroneous entry, etc.)..."
              helperText="This reason will be logged in the audit trail"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          gap: 1.5
        }}>
        <Tooltip title={cancelDisabledHint} disableHoverListener={!cancelDisabledHint} arrow>
          <span>
            <Button
              onClick={onClose}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.875rem',
                px: 2.5,
                py: 1
              }}>
              {isProcessing ? 'Cancelling...' : 'Cancel'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={confirmDisabledHint} disableHoverListener={!confirmDisabledHint} arrow>
          <span>
            <Button
              variant="contained"
              onClick={() => onConfirm(reasonDraft)}
              disabled={
                !reasonDraft.trim() ||
                isProcessing ||
                (showTransferSelection && (!selectedTransferSSCCId || !selectedTransferReceiptedEvent))
              }
              startIcon={
                isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon sx={{ fontSize: 18 }} />
              }
              sx={{
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                backgroundColor: '#d32f2f',
                '&:hover': { backgroundColor: '#c62828' },
                boxShadow: 'none',
                borderRadius: 1.5
              }}>
              {isProcessing ? 'Processing...' : showTransferSelection ? 'Move ASN to Selected SSCC' : 'Delete ASN'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
